import { useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { pdfPageMetaSelector } from 'apps/studio/store/selectors/pdfPageMeta'
import {
  entriesSelector,
  sheetsSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import { activeEntryGuidSelector } from 'apps/formsCreationKit/store/selectors/settings'
import {
  pagesSelector,
  activePageSelector,
  visibleStyledElementsWorkspaceSelector,
  layoutsSelector,
} from 'apps/studio/store/selectors/pdf'
import {
  OutlineGroup,
  OutlineItem,
} from 'apps/studio/components/shared/Outline'
import { selectElement } from 'apps/studio/store/actions/sharedActions'
import { appSettingsActions } from 'apps/studio/store/reducers/settings/appSettings'
import { lib } from '@gocanvas/client'
const { selectSheet } = appSettingsActions
const { LOOP_SHEET_TYPE_ID } = lib

const PdfTableOutlineGroup = ({ tableLayout, tableElements, pageNumber }) => {
  const dispatch = useDispatch()
  const entries = useSelector(entriesSelector)
  const sheets = useSelector(sheetsSelector)
  const activeEntryGuid = useSelector(activeEntryGuidSelector)

  const keyFieldSheet = sheets.find(
    sheet =>
      sheet.section_id === tableLayout.section_id &&
      sheet.sheet_type_id === LOOP_SHEET_TYPE_ID
  )
  const colCount = tableLayout.columns?.length || 1
  const headerElements = [...tableElements]
    .filter(element => element.sort_order <= colCount)
    .sort((a, b) => a.sort_order - b.sort_order)
  const tableTitle =
    keyFieldSheet?.description || keyFieldSheet?.placeholder || 'Table'

  return (
    <OutlineGroup
      title={tableTitle}
      type="table"
      isActive={false}
      setIsActive={() =>
        dispatch(selectSheet({ sheet_id: keyFieldSheet?.id }))
      }
      pageNumber={pageNumber}
    >
      {headerElements.map(element => {
        const entry = entries.find(({ guid }) => guid === element.entry_guid)
        if (!entry) return null

        return (
          <OutlineItem
            key={`outline-table-col-${element.entry_guid}`}
            type={element.element_type}
            label={entry.label}
            isActive={activeEntryGuid === element.entry_guid}
            setIsActive={() =>
              dispatch(
                selectElement({
                  entry_guid: element.entry_guid,
                  sheet_id: entry.sheet_id,
                })
              )
            }
            pageNumber={pageNumber}
          />
        )
      })}
    </OutlineGroup>
  )
}

export const PdfOutline = () => {
  const dispatch = useDispatch()
  const allPages = useSelector(pagesSelector)
  const pageMeta = useSelector(pdfPageMetaSelector)
  const elements = useSelector(visibleStyledElementsWorkspaceSelector)
  const entries = useSelector(entriesSelector)
  const sheets = useSelector(sheetsSelector)
  const activePage = useSelector(activePageSelector)
  const activeEntryGuid = useSelector(activeEntryGuidSelector)
  const layouts = useSelector(layoutsSelector)
  const activePageNumber = activePage?.page_number

  const selectEntry = useCallback(
    (entry_guid, sheet_id) =>
      dispatch(selectElement({ entry_guid, sheet_id })),
    [dispatch]
  )

  const pages = allPages.map(page => {
    const meta = pageMeta.find(m => m.pageNumber === page.page_number) || {}
    return {
      ...meta,
      ...page,
      pageNumber: page.page_number,
    }
  })

  // TODO: We might benefit from standardizing the panel markup into it's
  // own component now that we can see where the repeated markup occurs
  return (
    <div className="gcds--control-panel workspace-panel">
      <div className="panel--info is-sticky">
        <div className="panel--title">PDF Outline</div>
      </div>
      <div className="panel--content --workspace-panel--content">
        {pages.map(page => {
          const { id: pageId, pageNumber } = page
          const pageLayouts = layouts.filter(layout => layout.page_id === pageId)
          const tableLayouts = pageLayouts.filter(
            layout => layout.layout_type === 'table'
          )
          const tableLayoutIds = new Set(tableLayouts.map(layout => layout.id))
          const topLevelLayout = pageLayouts.find(layout => !layout.parent_layout_id)
          const { id: sheetId } =
            sheets.find(sheet => sheet.section_id === topLevelLayout?.section_id) || {}
          const isActivePage =
            activePageNumber === pageNumber && !activeEntryGuid
          const pageElements = elements.filter(
            element => element.page_number === pageNumber
          )
          const nonTableElements = pageElements.filter(
            element => !tableLayoutIds.has(element.layout_id)
          )

          return (
            <OutlineGroup
              key={`page-${pageNumber}-outline`}
              title={`Page ${pageNumber}`}
              isActive={isActivePage}
              setIsActive={() => dispatch(selectSheet({ sheet_id: sheetId }))}
              type="page"
              pageNumber={pageNumber}
            >
              {nonTableElements.length > 0 &&
                nonTableElements.map(element => {
                  const { entry_guid, element_type } = element
                  const entry = entries.find(e => e.guid === entry_guid)
                  const { label, sheet_id: entrySheetId } = entry
                  return (
                    <OutlineItem
                      key={`outline-element-${entry_guid}`}
                      type={element_type}
                      label={label}
                      isActive={activeEntryGuid === entry_guid}
                      setIsActive={() => selectEntry(entry_guid, entrySheetId)}
                      pageNumber={pageNumber}
                    />
                  )
                })}
              {tableLayouts.map(tableLayout => {
                const tableElements = pageElements.filter(
                  element => element.layout_id === tableLayout.id
                )
                if (!tableElements.length) return null

                return (
                  <PdfTableOutlineGroup
                    key={`table-${tableLayout.id}-outline`}
                    tableLayout={tableLayout}
                    tableElements={tableElements}
                    pageNumber={pageNumber}
                  />
                )
              })}
            </OutlineGroup>
          )
        })}
      </div>
    </div>
  )
}
